/**
 * Unified notification system combining goey-toast (blob morphing) + soundcn (kaiju sounds).
 * Drop-in replacement for `toast.success/error/warning/info` from sonner.
 *
 * Usage:
 *   import { notify } from '@/lib/notify'
 *   notify.success('Customer saved')
 *   notify.error('Generation failed', { description: err.message })
 *   notify.aiGeneration(promise, { loading: 'Generating...', success: 'Done!', error: 'Failed' })
 */

import { goeyToast, type GoeyToastOptions, type GoeyPromiseData } from 'goey-toast'
import { playSound } from '@/lib/sound-engine'

// ── Neon cyberpunk theme presets ────────────────────────────────────
const NEON = {
  success: { fillColor: '#0a0f0a', borderColor: '#84cc16', borderWidth: 1.5 },
  error: { fillColor: '#1a0a0a', borderColor: '#ef4444', borderWidth: 1.5 },
  warning: { fillColor: '#1a150a', borderColor: '#f97316', borderWidth: 1.5 },
  info: { fillColor: '#0a0a1a', borderColor: '#3b82f6', borderWidth: 1.5 },
} as const

// ── Sound preference ────────────────────────────────────────────────
const SOUND_KEY = 'redtape-sounds'

function isSoundEnabled(): boolean {
  return localStorage.getItem(SOUND_KEY) !== 'false'
}

export function setSoundEnabled(enabled: boolean): void {
  localStorage.setItem(SOUND_KEY, enabled ? 'true' : 'false')
}

export function getSoundEnabled(): boolean {
  return isSoundEnabled()
}

// ── Sound assets (lazy-loaded to keep bundle small) ─────────────────
// Each sound file is a ~1-22KB TS module with inline base64 data URI.
// We dynamic-import them so they don't bloat the initial chunk.
type SoundLoader = () => Promise<{ default?: { dataUri: string }; [key: string]: any }>

const sounds = {
  success: () => import('@/sounds/confirmation-001'),
  error: () => import('@/sounds/error-001'),
  powerUp: () => import('@/sounds/power-up-1'),
  explosion: () => import('@/sounds/low-frequency-explosion-000'),
  forceField: () => import('@/sounds/force-field-000'),
  glitch: () => import('@/sounds/glitch-001'),
  impact: () => import('@/sounds/impact-metal-heavy-000'),
  crunch: () => import('@/sounds/explosion-crunch-000'),
} satisfies Record<string, SoundLoader>

async function playSfx(
  loader: SoundLoader,
  opts?: { volume?: number; playbackRate?: number }
): Promise<void> {
  if (!isSoundEnabled()) return
  try {
    const mod = await loader()
    // Sound files export a named const (e.g., confirmation001Sound)
    const asset = Object.values(mod).find(
      (v): v is { dataUri: string } => v && typeof v === 'object' && 'dataUri' in v
    )
    if (asset) {
      playSound(asset.dataUri, {
        volume: opts?.volume ?? 0.5,
        playbackRate: opts?.playbackRate ?? 1,
      })
    }
  } catch {
    // Sound is non-critical — never block UX
  }
}

// ── Extended options (GoeyToastOptions + our extras) ────────────────
export interface NotifyOptions extends Omit<GoeyToastOptions, 'fillColor' | 'borderColor' | 'borderWidth'> {
  /** Skip playing sound for this notification */
  silent?: boolean
}

// ── Public API ──────────────────────────────────────────────────────
export const notify = {
  success(title: string, opts?: NotifyOptions) {
    if (!opts?.silent) playSfx(sounds.success)
    return goeyToast.success(title, { ...NEON.success, ...opts })
  },

  error(title: string, opts?: NotifyOptions) {
    if (!opts?.silent) playSfx(sounds.error)
    return goeyToast.error(title, { ...NEON.error, ...opts })
  },

  warning(title: string, opts?: NotifyOptions) {
    if (!opts?.silent) playSfx(sounds.glitch, { volume: 0.4 })
    return goeyToast.warning(title, { ...NEON.warning, ...opts })
  },

  info(title: string, opts?: NotifyOptions) {
    return goeyToast.info(title, { ...NEON.info, ...opts })
  },

  /**
   * Promise toast for AI generation flows (10-30s waits).
   * Shows loading blob → success/error blob with sounds.
   *
   * Usage:
   *   notify.aiGeneration(generateDapStrategy(customerId), {
   *     loading: 'Generating DAP Strategy...',
   *     success: 'DAP Strategy ready!',
   *     error: 'Generation failed',
   *   })
   */
  aiGeneration<T>(promise: Promise<T>, data: GoeyPromiseData<T>) {
    playSfx(sounds.forceField, { volume: 0.3 })
    // Attach a then handler to play success/error sounds
    promise
      .then(() => playSfx(sounds.explosion, { volume: 0.4, playbackRate: 0.7 }))
      .catch(() => playSfx(sounds.error, { volume: 0.5 }))
    return goeyToast.promise(promise, {
      ...data,
      ...NEON.info,
    })
  },

  /** Dismiss a specific toast or all toasts */
  dismiss(id?: string | number) {
    return goeyToast.dismiss(id)
  },

  /** Play a one-off sound effect without a toast */
  sfx: {
    click: () => playSfx(sounds.success, { volume: 0.2, playbackRate: 1.5 }),
    impact: () => playSfx(sounds.impact, { volume: 0.4 }),
    powerUp: () => playSfx(sounds.powerUp, { volume: 0.4 }),
    kaijuStomp: () => playSfx(sounds.explosion, { volume: 0.5, playbackRate: 0.5 }),
  },
}
